import { RETRYABLE_HTTP_STATUS_CODES } from '@blufin/types';
import type { HttpMethod, IHttpClientOptions, ServiceAuthMethod } from '@blufin/types';

export interface HttpClientHmacCacheEntry {
  fetchedAt: number; // Timestamp when token was fetched (Date.now())
  hmacToken: string;
  timestamp: string;
  timestampOffset: number;
}

export interface HttpClientPendingRequest {
  authMethod: string;
  method: string;
  options?: IHttpClientOptions;
  payload?: any;
  reject: (reason?: unknown) => void;
  requestId?: string; // UUID for request tracking and log correlation
  resolve: (value: unknown) => void;
  retryCount?: number;
  service: string;
  url: string;
}

export interface WorkerRequestMessage {
  authMethod: ServiceAuthMethod;
  hmacTimestampOffset?: number;
  hmacToken?: string;
  id: string;
  jwtToken?: string;
  method: HttpMethod;
  options?: IHttpClientOptions;
  payload?: any;
  requestId?: string; // UUID for request tracking and log correlation
  retryAttempt?: number; // Current retry attempt number (for logging)
  type?: 'request'; // Discriminant for WorkerInboundMessage union
  url: string; // Full URL address, including protocol, eg: https://www.some-domain.com/path/to/endpoint
}

/** Message sent from main thread to worker to cancel an in-flight request. */
export interface WorkerCancelMessage {
  id: string;
  type: 'cancel';
}

/** Union of all messages the worker can receive from the main thread. */
export type WorkerInboundMessage = WorkerCancelMessage | WorkerRequestMessage;

export interface WorkerResponseMessage {
  data?: any;
  error?: string;
  executionTimeMs: number;
  headers?: Record<string, string>;
  id: string;
  networkError?: boolean; // Indicates the failure was a network/connectivity error (no HTTP response). Hook for future offline detection — retries are skipped when true.
  requestId?: string;
  responseBytes?: number; // Length of the response body text (success messages only) — feeds the startup request ledger
  statusCode?: number | null;
  success: boolean;
  timedOut?: boolean; // Indicates if the request was aborted due to timeout
  wasResponseEncrypted?: boolean; // Indicates if the response payload was decrypted by the worker
}

/**
 * Configuration for HTTP request retry behavior with exponential backoff.
 */
export interface HttpRetryConfig {
  /** Maximum number of retry attempts. Default: 3 */
  baseDelayMs: number;
  /** Base delay in milliseconds for exponential backoff. Default: 1000 */
  maxDelayMs: number;
  /** Maximum delay in milliseconds between retries. Default: 10000 */
  maxRetries: number;
  /** HTTP status codes that should trigger a retry. Default: [408, 429, 500, 502, 503, 504] */
  retryableStatusCodes: number[];
}

/** Default retry configuration values. */
export const DEFAULT_RETRY_CONFIG: HttpRetryConfig = {
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  maxRetries: 3,
  retryableStatusCodes: [...RETRYABLE_HTTP_STATUS_CODES]
};
